'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/shadcn/lib/utils';
import { Listing } from '../types';
import { formatPriceToShort } from '@/features/adverts/utils';
import bounds from '@/public/images/demo/map-ba.json';

/**
 * DEMO MAPA - docasna nahrada za Mapbox, kym nemame realny token.
 *
 * Staticky render Carto Positron (public/images/demo/map-ba.jpg) + piny
 * pocitane Web Mercator projekciou z bounds v map-ba.json. Obrazok sa
 * sprava ako object-cover (stage vzdy pokryje kontajner, piny sedia).
 *
 * ZMAZAT / prepnut na ListingsMap hned ako pride MAPBOX_TOKEN.
 */

type Props = {
    className?: string;
    listings: Listing[];
    activeListingId?: string | null;
    onActiveChange?: (id: string | null) => void;
};

const merc = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const MN = merc(bounds.n);
const MS = merc(bounds.s);
const RATIO = bounds.width / bounds.height;

function project(lng: number, lat: number) {
    const x = (lng - bounds.w) / (bounds.e - bounds.w);
    const y = (MN - merc(lat)) / (MN - MS);
    return { x, y };
}

export function DemoMap(props: Props) {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const [stage, setStage] = useState({ w: 0, h: 0, x: 0, y: 0 });

    // stage = obrazok v object-cover rezime, centrovany
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            let sw: number;
            let sh: number;
            if (w / h > RATIO) {
                sw = w;
                sh = w / RATIO;
            } else {
                sh = h;
                sw = h * RATIO;
            }
            setStage({ w: sw, h: sh, x: (w - sw) / 2, y: (h - sh) / 2 });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={wrapRef}
            className={cn('relative w-full h-full overflow-hidden bg-[#f2f1ed]', props.className)}
            onMouseLeave={() => props.onActiveChange?.(null)}
        >
            <div
                className="absolute"
                style={{ width: stage.w, height: stage.h, left: stage.x, top: stage.y }}
            >
                <img
                    src="/images/demo/map-ba.jpg"
                    alt=""
                    draggable={false}
                    className="w-full h-full block select-none"
                />

                {props.listings.map((l) => {
                    if (l.longitude == null || l.latitude == null) return null;
                    const { x, y } = project(l.longitude, l.latitude);
                    const active = props.activeListingId === l.id;
                    const price = l.priceOnRequest
                        ? 'Dohodou'
                        : `${formatPriceToShort(l.finalPrice ?? 0)} €`;
                    return (
                        <a
                            key={l.id}
                            href={`/inzerat/${l.id}/${l.slug}`}
                            className={cn(
                                'absolute -translate-x-1/2 -translate-y-full',
                                'flex items-center h-[26px] px-[9px] rounded-[13px]',
                                'text-[12px] font-[700] leading-none whitespace-nowrap',
                                'shadow-[0_4px_12px_-2px_rgba(0,0,0,0.28)]',
                                'transition duration-300 [transition-timing-function:cubic-bezier(.22,.61,.36,1)]',
                                'hover:scale-[1.08] hover:z-[20]',
                                active
                                    ? 'bg-black text-[#F3C300] scale-[1.12] z-[20]'
                                    : 'bg-[#F3C300] text-black z-[10]',
                            )}
                            style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
                            onMouseEnter={() => props.onActiveChange?.(l.id)}
                        >
                            {price}
                            <span
                                aria-hidden
                                className={cn(
                                    'absolute left-1/2 -translate-x-1/2 top-full',
                                    'border-x-[5px] border-x-transparent border-t-[6px]',
                                    active ? 'border-t-black' : 'border-t-[#F3C300]',
                                )}
                            />
                        </a>
                    );
                })}
            </div>

            {/* demo watermark - zmaznut s prechodom na Mapbox */}
            <div className="absolute left-[12px] bottom-[10px] text-[10px] font-[600] tracking-[0.04em] uppercase text-black/40 pointer-events-none">
                Demo mapa · čaká na Mapbox token
            </div>
        </div>
    );
}
